import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import Icon from "../../../components/Icon";
import { showToast } from "../../../utils/toast";
import { ACCEPT, formatBytes, isImageFile, validateFiles } from "./attachments";

const MAX = 2000;

let uidSeq = 0;

function Composer({ replyTo, onCancelReply, editing, onCancelEdit, onSend, onSaveEdit }, ref) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // [{ id, file, preview }]
  const [showEmoji, setShowEmoji] = useState(false);
  const taRef = useRef(null);
  const emojiWrapRef = useRef(null);
  const fileInputRef = useRef(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  // Merge newly picked/dropped files into the staged tray (with validation).
  const addFiles = (fileList) => {
    const { accepted, errors } = validateFiles(fileList, filesRef.current.length);
    errors.forEach((e) => showToast("error", e));
    if (!accepted.length) return;
    setFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        id: `f${uidSeq++}`,
        file,
        preview: isImageFile(file) ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  // Let the drag-and-drop overlay (ChatPage) push files into this tray.
  useImperativeHandle(ref, () => ({ addFiles }));

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const clearFiles = () => {
    filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
  };

  // Revoke any outstanding preview URLs on unmount.
  useEffect(() => () => {
    filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
  }, []);

  // Dismiss the emoji picker on outside-click or Escape.
  useEffect(() => {
    if (!showEmoji) return undefined;
    const onDown = (e) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target)) setShowEmoji(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setShowEmoji(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [showEmoji]);

  // Load the message body when entering edit mode.
  useEffect(() => {
    if (editing) {
      setText(editing.body || "");
      taRef.current?.focus();
    }
  }, [editing]);

  // Auto-grow the textarea.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(160, el.scrollHeight) + "px";
  }, [text]);

  const over = text.length > MAX;
  // Attachments only apply to new messages, not edits.
  const canSend = !over && (text.trim().length > 0 || (!editing && files.length > 0));

  const submit = () => {
    if (!canSend) return;
    if (editing) {
      onSaveEdit(editing, text.trim());
    } else {
      onSend(text.trim(), files.map((f) => f.file));
      clearFiles();
    }
    setText("");
    setShowEmoji(false);
  };

  const onPick = (e) => {
    addFiles(e.target.files);
    e.target.value = ""; // allow re-picking the same file
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape") {
      if (editing) onCancelEdit();
      else if (replyTo) onCancelReply();
    }
  };

  const insertEmoji = (emojiData) => {
    const el = taRef.current;
    const emoji = emojiData.emoji;
    if (!el) { setText((t) => t + emoji); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emoji + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + emoji.length;
    });
  };

  return (
    <div className="chat-composer">
      {editing && (
        <div className="chat-composer-banner">
          <Icon name="edit" size={13} />
          <span>Editing message</span>
          <button type="button" onClick={onCancelEdit}><Icon name="x" size={13} /></button>
        </div>
      )}
      {replyTo && !editing && (
        <div className="chat-composer-banner">
          <Icon name="arrow-left" size={13} />
          <span className="chat-reply-preview">Replying to <b>{replyTo.sender?.name || "message"}</b>: {replyTo.body?.slice(0, 80)}</span>
          <button type="button" onClick={onCancelReply}><Icon name="x" size={13} /></button>
        </div>
      )}

      {!editing && files.length > 0 && (
        <div className="chat-attach-tray">
          {files.map((f) => (
            <div key={f.id} className={`chat-attach-chip ${f.preview ? "image" : "file"}`}>
              {f.preview ? (
                <img src={f.preview} alt={f.file.name} />
              ) : (
                <span className="chat-attach-file">
                  <Icon name="file-text" size={16} />
                  <span className="chat-attach-name">{f.file.name}</span>
                </span>
              )}
              <span className="chat-attach-size">{formatBytes(f.file.size)}</span>
              <button type="button" className="chat-attach-remove" title="Remove" onClick={() => removeFile(f.id)}>
                <Icon name="x" size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="chat-composer-row">
        <div className="chat-emoji-wrap" ref={emojiWrapRef}>
          <button type="button" className={`chat-emoji-btn ${showEmoji ? "active" : ""}`} title="Emoji" onClick={() => setShowEmoji((v) => !v)}>
            <Icon name="smile" size={20} />
          </button>
          {showEmoji && (
            <div className="chat-emoji-pop">
              <div className="chat-emoji-head">
                <span>Emoji</span>
                <button type="button" title="Close" onClick={() => setShowEmoji(false)}><Icon name="x" size={14} /></button>
              </div>
              <EmojiPicker onEmojiClick={insertEmoji} width={320} height={360} previewConfig={{ showPreview: false }} />
            </div>
          )}
        </div>

        {!editing && (
          <>
            <button type="button" className="chat-attach-btn" title="Attach files" onClick={() => fileInputRef.current?.click()}>
              <Icon name="paperclip" size={19} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT}
              style={{ display: "none" }}
              onChange={onPick}
            />
          </>
        )}

        <textarea
          ref={taRef}
          className="chat-input"
          placeholder="Type a message…  (Enter to send, Shift+Enter for newline)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
        />

        <div className="chat-composer-actions">
          <span className={`chat-counter ${over ? "over" : ""}`}>{text.length}/{MAX}</span>
          <button type="button" className="btn btn-primary btn-sm" disabled={!canSend} onClick={submit}>
            <Icon name="send" size={13} /> {editing ? "Save" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default forwardRef(Composer);
